import classNames from "classnames";
import { useFormik } from "formik";
import { AscButton } from "../../components";
import { GoogleIcon } from "../../components/AscIcons";
import AscPasswordInput from "../../components/AscInputFields/AscPasswordInput/AscPasswordInput";
import AscTextInput from "../../components/AscInputFields/AscTextInput/AscTextInput";
import AscLink from "../../components/AscLink/AscLink";
import AscHeading from "../../components/AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../components/AscTypography/AscParagraph/AscParagraph";
import AscAuthFlowLayout from "../AscAuthFlowLayout/AscAuthFlowLayout";
import styles from "./AscSignUpView.module.scss";
import * as Yup from "yup";
import YupPassword from "yup-password";
YupPassword(Yup);

interface AscUserRegisterInfo {
  name: string;
  organization: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface AscSignUpViewProps {
  className?: string;
  signInLink: string;
  onSignUp: (userRegisterInfo: AscUserRegisterInfo) => void;
  googleSignInLink: string;
  loading?: boolean;
}

export default function AscSignUpView({
  className,
  signInLink,
  googleSignInLink,
  onSignUp,
  loading,
}: AscSignUpViewProps) {
  const formik = useFormik({
    initialValues: {
      name: "",
      organization: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      organization: Yup.string().required("Organization is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .required("Password is required")
        .min(
          6,
          "password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special character"
        )
        .minLowercase(1, "password must contain at least 1 lower case letter")
        .minUppercase(1, "password must contain at least 1 upper case letter")
        .minNumbers(1, "password must contain at least 1 number")
        .minSymbols(1, "password must contain at least 1 special character"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords don't match")
        .required("You must confirm your password"),
    }),
    onSubmit: (values) => {
      onSignUp(values);
    },
  });

  return (
    <AscAuthFlowLayout alignment="right" roundSideImage>
      <form
        onSubmit={formik?.handleSubmit}
        data-testid="asc-sign-up-view"
        className={classNames(styles.ground, className)}
      >
        <header className={styles.heading}>
          <AscHeading className={styles.title} level={1}>
            Welcome to The Edge
          </AscHeading>
          <AscParagraph className={styles.subTitle}>
            Let's start upskilling! Find your competitive edge today!
          </AscParagraph>
        </header>

        <div className={styles.form}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="name">
              Name
            </label>
            <AscTextInput
              className={styles.inputField}
              placeholder="James Jones"
              id="name"
              name="name"
              data-testid="asc-sign-up-name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
            />
            {formik.errors.name && formik.touched.name && (
              <div className={styles.fieldError}>{formik.errors.name}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="organization">
              School
            </label>
            <AscTextInput
              className={styles.inputField}
              placeholder="LA Art School"
              id="organization"
              name="organization"
              data-testid="asc-sign-up-organization"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.organization}
            />
            {formik.errors.organization && formik.touched.organization && (
              <div className={styles.fieldError}>
                {formik.errors.organization}
              </div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="email">
              Email
            </label>
            <AscTextInput
              className={styles.inputField}
              placeholder="jamesjones@gmail.com"
              id="email"
              name="email"
              data-testid="asc-sign-up-email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
            />
            {formik.errors.email && formik.touched.email && (
              <div className={styles.fieldError}>{formik.errors.email}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="password">
              Password
            </label>
            <AscPasswordInput
              className={styles.inputField}
              placeholder="1134nnnk123j"
              id="password"
              name="password"
              data-testid="asc-sign-up-password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
            />
            {formik.errors.password && formik.touched.password && (
              <div className={styles.fieldError}>{formik.errors.password}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="confirmPassword">
              Confirm Password
            </label>
            <AscPasswordInput
              className={styles.inputField}
              placeholder="1134nnnk123j"
              id="confirmPassword"
              name="confirmPassword"
              data-testid="asc-sign-up-confirm-password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirmPassword}
            />
            {formik.errors.confirmPassword &&
              formik.touched.confirmPassword && (
                <div className={styles.fieldError}>
                  {formik.errors.confirmPassword}
                </div>
              )}
          </div>
        </div>

        <footer className={styles.footer}>
          <AscButton
            className={styles.footerButton}
            variant="primary"
            size="large"
            fullWidth
            type="submit"
            data-testid="asc-sign-up-submit"
            loading={loading}
          >
            Sign up
          </AscButton>
          {/* <AscLink innerWrapperTag={null} href={googleSignInLink}>
            <AscButton
              className={classNames(styles.footerButton, styles.googleButton)}
              variant="primary"
              size="large"
              fullWidth
              type="button"
              narrowPadding
              icon={<GoogleIcon className={styles.googleIcon} />}
            >
              Sign up with Google
            </AscButton>
          </AscLink> */}

          <div className={styles.signInPrompt}>
            Already have an account?{" "}
            <AscLink
              className={styles.signInLink}
              href={signInLink}
              innerWrapperTag="button"
            >
              Sign in
            </AscLink>
          </div>
        </footer>
      </form>
    </AscAuthFlowLayout>
  );
}
